import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, X, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { UserMenu } from './UserMenu';
import { LoginModal } from './LoginModal';
import { ThemeToggle } from './ThemeToggle';

interface SimpleHeaderProps {
  isHomePage?: boolean;
}

const SimpleHeader: React.FC<SimpleHeaderProps> = ({ isHomePage = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useParams<{ lang?: string }>();
  const [showLegacyDialog, setShowLegacyDialog] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const langDropdownRef = useRef<HTMLDivElement>(null);

  // Extract language from URL path as fallback
  const pathParts = location.pathname.split('/').filter(part => part !== '');
  const langFromPath = pathParts.length > 0 && ['en', 'fr', 'de', 'it'].includes(pathParts[0])
    ? pathParts[0] as 'en' | 'fr' | 'de' | 'it'
    : 'en';

  const currentLang = (lang as 'en' | 'fr' | 'de' | 'it') || langFromPath;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target as Node)) {
        setShowLangDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const translations = {
    back: {
      en: 'Back to search',
      fr: 'Retour à la recherche',
      de: 'Zurück zur Suche',
      it: 'Torna alla ricerca'
    },
    legacy: {
      title: {
        en: 'Switch to Legacy Version',
        fr: 'Passer à la version classique',
        de: 'Zur klassischen Version wechseln',
        it: 'Passa alla versione classica'
      },
      description: {
        en: 'The original TTStats experience with all historical data and classic features.',
        fr: 'L\'expérience TTStats originale avec toutes les données historiques et fonctionnalités classiques.',
        de: 'Die ursprüngliche TTStats-Erfahrung mit allen historischen Daten und klassischen Funktionen.',
        it: 'L\'esperienza TTStats originale con tutti i dati storici e le funzionalità classiche.'
      },
      goButton: {
        en: 'Go to Original',
        fr: 'Aller à l\'original',
        de: 'Zum Original',
        it: 'Vai all\'originale'
      },
      stayButton: {
        en: 'Stay in Beta',
        fr: 'Rester en Beta',
        de: 'Im Beta bleiben',
        it: 'Rimani in Beta'
      }
    }
  };
  
  const handleLanguageChange = (newLang: string) => {
    // Replace the current language in the path with the new language
    const pathParts = location.pathname.split('/').filter(part => part !== '');
    
    // Check if first part is a language code
    if (pathParts.length > 0 && ['en', 'fr', 'de', 'it'].includes(pathParts[0])) {
      // Replace existing language
      pathParts[0] = newLang;
    } else {
      // Add language at the beginning
      pathParts.unshift(newLang);
    }
    
    const newPath = '/' + pathParts.join('/');
    navigate(newPath);
  };
  
  return (
    <>
      <motion.div
        className="fixed top-0 left-0 w-full z-30 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border-b border-gray-100 dark:border-gray-800 transition-colors duration-300"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{
          duration: 0.4,
          ease: [0.4, 0, 0.2, 1]
        }}
      >
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
          {/* Left side - Back button or Beta indicator */}
          <div className="flex items-center">
            {!isHomePage ? (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <Link
                  to={`/${currentLang}`}
                  className="inline-flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition group"
                >
                  <motion.div
                    className="mr-1"
                    whileHover={{ x: -2 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </motion.div>
                  <span className="hidden md:inline">
                    {translations.back[currentLang] || translations.back.en}
                  </span>
                </Link>
              </motion.div>
            ) : (
              // Empty div to maintain layout balance on homepage
              <div className="w-16" />
            )}
          </div>
          
          {/* Center spacer for layout balance */}
          <div className="flex-1" />

          {/* Language Switcher, Theme Toggle, and User Menu on the right */}
          <div className="flex items-center gap-2">
            {/* Language Switcher with Globe Icon */}
            <motion.div
              ref={langDropdownRef}
              className="relative"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.4,
                delay: 0.1,
                ease: [0.4, 0, 0.2, 1]
              }}
            >
              <motion.button
                onClick={() => setShowLangDropdown(!showLangDropdown)}
                className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Globe className="h-4 w-4" />
                <span className="text-xs font-medium uppercase">{currentLang}</span>
              </motion.button>

              <AnimatePresence>
                {showLangDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 mt-1 py-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 min-w-[100px] z-50"
                  >
                    {(['en', 'fr', 'de', 'it'] as const).map((langOption) => (
                      <button
                        key={langOption}
                        onClick={() => {
                          handleLanguageChange(langOption);
                          setShowLangDropdown(false);
                        }}
                        className={`w-full px-3 py-1.5 text-left text-xs font-medium transition-colors ${
                          currentLang === langOption
                            ? 'bg-[#1C7AD6] text-white'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        {langOption === 'en' && 'English'}
                        {langOption === 'fr' && 'Français'}
                        {langOption === 'de' && 'Deutsch'}
                        {langOption === 'it' && 'Italiano'}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Theme Toggle */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.4,
                delay: 0.15,
                ease: [0.4, 0, 0.2, 1]
              }}
            >
              <ThemeToggle />
            </motion.div>

            {/* User Menu */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.4,
                delay: 0.2,
                ease: [0.4, 0, 0.2, 1]
              }}
            >
              <UserMenu onOpenLogin={() => setShowLoginModal(true)} currentLang={currentLang} />
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>

    {/* Login Modal */}
    <LoginModal
      isOpen={showLoginModal}
      onClose={() => setShowLoginModal(false)}
      currentLang={currentLang}
    />
      
    {/* Legacy Version Dialog - Outside of header for proper fullscreen coverage */}
    <AnimatePresence>
      {showLegacyDialog && (
        <>
          {/* Backdrop - covers entire screen */}
          <motion.div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[100]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowLegacyDialog(false)}
          />
          
          {/* Dialog - centered in viewport */}
          <motion.div
            className="fixed inset-0 z-[101] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowLegacyDialog(false)}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl overflow-hidden w-full max-w-md"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="relative bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {translations.legacy.title[currentLang] || translations.legacy.title.en}
                </h3>
                <button
                  onClick={() => setShowLegacyDialog(false)}
                  className="absolute top-4 right-4 p-1 rounded-lg hover:bg-gray-200/50 transition-colors"
                >
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>
              
              {/* Content */}
              <div className="px-6 py-5">
                <p className="text-sm text-gray-600 mb-6">
                  {translations.legacy.description[currentLang] || translations.legacy.description.en}
                </p>
                
                {/* Actions */}
                <div className="flex gap-3">
                  <motion.a
                    href="https://ttstats.ch"
                    className="flex-1 px-4 py-2.5 bg-[#1C7AD6] hover:bg-[#1565C0] text-white text-sm font-medium rounded-lg text-center transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {translations.legacy.goButton[currentLang] || translations.legacy.goButton.en}
                  </motion.a>
                  <motion.button
                    onClick={() => setShowLegacyDialog(false)}
                    className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {translations.legacy.stayButton[currentLang] || translations.legacy.stayButton.en}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
    </>
  );
};

export default SimpleHeader;